import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ArrowLeft, Plus, Loader2, MapPin, Sparkles } from 'lucide-react';
import { sectionsApi } from '../services/api';
import FloorPlanEditor from '../components/FloorPlanEditor';

export default function FloorPlanPage() {
  const queryClient = useQueryClient();

  const { data: sectionsData, isLoading, refetch } = useQuery({
    queryKey: ['sections'],
    queryFn: async () => {
      const response = await sectionsApi.getAll();
      return response.data?.data || response.data || [];
    },
  });

  const initializeMutation = useMutation({
    mutationFn: (options?: { tableCount?: number; includePatio?: boolean; includeBar?: boolean }) =>
      sectionsApi.initialize(options),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['sections'] });
    },
  });

  const sections = sectionsData || [];

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <div className="bg-white border-b border-slate-200 sticky top-0 z-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <Link
                to="/host"
                className="p-2 rounded-lg hover:bg-slate-100 transition-colors"
              >
                <ArrowLeft className="w-5 h-5 text-slate-600" />
              </Link>
              <div>
                <h1 className="text-xl font-semibold text-slate-900">Floor Plan</h1>
                <p className="text-sm text-slate-500">Drag tables to rearrange, click to add new tables</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        {sections.length === 0 ? (
          <div className="card p-12 text-center">
            <MapPin className="w-16 h-16 text-slate-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-slate-900 mb-2">No Floor Plan Yet</h2>
            <p className="text-slate-500 mb-6 max-w-md mx-auto">
              Set up your restaurant's floor plan with sections and tables.
              You can customize the layout later.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <button
                onClick={() => initializeMutation.mutate({ tableCount: 10, includePatio: true, includeBar: true })}
                disabled={initializeMutation.isPending}
                className="btn btn-primary flex items-center gap-2"
              >
                {initializeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Sparkles className="w-4 h-4" />
                )}
                Quick Setup (10 tables)
              </button>
              <button
                onClick={() => initializeMutation.mutate({ tableCount: 20, includePatio: true, includeBar: true })}
                disabled={initializeMutation.isPending}
                className="btn btn-secondary flex items-center gap-2"
              >
                {initializeMutation.isPending ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <Plus className="w-4 h-4" />
                )}
                Full Setup (20 tables)
              </button>
            </div>

            {initializeMutation.isError && (
              <p className="mt-4 text-sm text-red-600">
                {(initializeMutation.error as any)?.response?.data?.error?.message || 'Failed to initialize floor plan'}
              </p>
            )}
          </div>
        ) : (
          <div className="card p-6">
            <FloorPlanEditor
              sections={sections}
              onUpdate={() => refetch()}
            />
          </div>
        )}

        {/* Help Section */}
        <div className="mt-6 card p-4 bg-slate-50 border-slate-200">
          <h3 className="font-medium text-slate-900 mb-2">Tips</h3>
          <ul className="text-sm text-slate-600 space-y-1">
            <li>• <strong>Click</strong> on the canvas to add a new table</li>
            <li>• <strong>Drag</strong> tables to reposition them</li>
            <li>• <strong>Select</strong> a table to edit capacity or delete</li>
            <li>• Use the <strong>section tabs</strong> to switch between areas</li>
            <li>• <strong>Save</strong> changes when you're done rearranging</li>
          </ul>
        </div>
      </div>
    </div>
  );
}
